//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { UnifiedCardData, formatDate, displayOrNA } from './UnifiedCardData';
import RelatedLinks from './RelatedLinks';
import PrIcon from '../../../icons/pr.svg';

export const PullRequestCard: React.FC<{ card: UnifiedCardData }> = ({
  card,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const formatDateLocal = formatDate;

  return (
    <div
      className="mb-6 rounded-lg bg-[#f2f4f5] p-6 dark:bg-[#393939]"
      id="id-pr-card-container"
      data-testid="pr-card-container"
    >
      <div
        className="mb-4 flex items-start gap-3"
        id="id-pr-card-content"
        data-testid="pr-card-content"
      >
        <div
          className="mt-0.5 shrink-0"
          id="id-pr-card-icon-wrapper"
          data-testid="pr-card-icon-wrapper"
        >
          <div
            className="flex size-8 items-center justify-center rounded"
            id="id-pr-card-icon"
            data-testid="pr-card-icon"
          >
            <img
              src={PrIcon}
              alt="Pull Request icon"
              className="size-8 object-contain"
            />
          </div>
        </div>
        <div
          className="min-w-0 flex-1"
          id="id-pr-card-details"
          data-testid="pr-card-details"
        >
          <div
            className="mb-1 flex items-center gap-2"
            id="id-pr-card-title-row"
            data-testid="pr-card-title-row"
          >
            <span
              className="text-[12px] text-gray-500 dark:text-gray-400"
              id="id-pr-card-type-label"
              data-testid="pr-card-type-label"
            >
              {t('pr')}
            </span>
            <span
              className="text-[12px] text-gray-300 dark:text-gray-600"
              id="id-pr-card-title-separator"
              data-testid="pr-card-title-separator"
            >
              |
            </span>
            <span
              className="cursor-default text-[14px] font-medium text-[#0043ce] dark:text-brand-interactiveDark"
              id="id-pr-card-title"
              data-testid="pr-card-title"
            >
              {card.cardTitle}
            </span>
          </div>
          <p className="mt-1 text-[12px] text-gray-600 dark:text-gray-300">
            <span
              id="id-pr-card-created-label"
              data-testid="pr-card-created-label"
            >
              {t('created')}:
            </span>{' '}
            <span
              id="id-pr-card-created-value"
              data-testid="pr-card-created-value"
            >
              {(() => {
                const formatted = formatDateLocal(card.created);
                return formatted === '--' ? t('not-available') : formatted;
              })()}
            </span>
            <span
              className="mx-1"
              id="id-pr-card-separator-1"
              data-testid="pr-card-separator-1"
            >
              |
            </span>
            <span
              id="id-pr-card-created-by-label"
              data-testid="pr-card-created-by-label"
            >
              {t('created-by')}:
            </span>
            <span
              className="ml-1"
              id="id-pr-card-created-by-value"
              data-testid="pr-card-created-by-value"
            >
              {displayOrNA(card.createdBy || card.owner, t)}
            </span>
            <span
              className="mx-1"
              id="id-pr-card-separator-2"
              data-testid="pr-card-separator-2"
            >
              |
            </span>
            <span
              id="id-pr-card-base-branch-label"
              data-testid="pr-card-base-branch-label"
            >
              {t('base-branch')}:
            </span>
            <span
              className="ml-1"
              id="id-pr-card-base-branch-value"
              data-testid="pr-card-base-branch-value"
            >
              {displayOrNA(card.baseBranch, t)}
            </span>
            <span
              className="mx-1"
              id="id-pr-card-separator-3"
              data-testid="pr-card-separator-3"
            >
              |
            </span>
            <span
              id="id-pr-card-repo-name-label"
              data-testid="pr-card-repo-name-label"
            >
              {t('repository-name')}:
            </span>
            <span
              className="ml-1"
              id="id-pr-card-repo-name-value"
              data-testid="pr-card-repo-name-value"
            >
              {displayOrNA(card.repositoryName, t)}
            </span>
            <span
              className="mx-1"
              id="id-pr-card-separator-4"
              data-testid="pr-card-separator-4"
            >
              |
            </span>
            <span
              id="id-pr-card-repo-owner-label"
              data-testid="pr-card-repo-owner-label"
            >
              {t('repository-owner')}:
            </span>
            <span
              className="ml-1"
              id="id-pr-card-repo-owner-value"
              data-testid="pr-card-repo-owner-value"
            >
              {displayOrNA(card.repositoryOwner, t)}
            </span>
          </p>
          {card.description && (
            <p className="mt-3 text-[12px] leading-5 text-gray-700 dark:text-gray-200">
              {displayOrNA(card.description, t)}
            </p>
          )}

          {/* Links Related - Accordion */}
          <div
            className="mt-4 border-t border-gray-300 pt-4 dark:border-gray-600"
            id="id-pr-card-links-section"
            data-testid="pr-card-links-section"
          >
            <button
              onClick={() => setExpanded((prev) => !prev)}
              className="flex items-center gap-2 text-[12px] text-[#0043ce] transition-colors hover:underline dark:text-brand-interactiveDark"
            >
              <span
                id="id-pr-card-links-label"
                data-testid="pr-card-links-label"
              >
                {t('links-related-result')}
              </span>
              <ChevronDown
                size={16}
                className={`transition-transform ${expanded ? 'rotate-180' : ''}`}
              />
            </button>
            {expanded && (
              <RelatedLinks card={card} primaryLabel="pull-request-url" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
